import axios, { AxiosError, AxiosResponse } from 'axios';

import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import localStorageWrapper from 'common/loginUtils/local-storage-wrapper';
import storageWrapper from 'common/loginUtils/storage-wrapper';
import { storageCleanAndRedirect } from 'common/utils/authCheck';
import { urlWithTenant } from 'common/utils/utilFunctions';

import { CONTENT_TYPE, DEFAULT_TENANT_ID } from '../constants';

export interface ApiResponse<T, E> {
	/**
	 * HTTP status code
	 */
	statusCode: number;
	/**
	 * If error has been caught or not
	 */
	isError: boolean;
	/**
	 * Response body
	 */
	responseBody?: T;
	/**
	 * Response body in case of error
	 */
	errorBody?: E;
	/**
	 * Error object caught by catch block, use it for debugging
	 */
	error?: unknown;
	headers?: any;
}

const { FORBIDDEN, UNAUTHORIZED } = HTTP_STATUS_CODES;

enum Method {
	GET = 'GET',
	POST = 'POST',
	PUT = 'PUT',
	PATCH = 'PATCH',
	DELETE = 'DELETE',
}
axios.interceptors.request.use((config: any) => {
	const idKey = 'id-token';
	const token = storageWrapper.getStorageItem('access-token');
	const acessToken = `Bearer ${token}`;
	config.headers.Authorization = acessToken;
	config.headers[idKey] = `${storageWrapper.getStorageItem('id-token')}`;
	const tenantToken = 'X-TENANT-ID';
	const tenantId =
		localStorageWrapper.getStorageItem('tenantId') || DEFAULT_TENANT_ID;

	config.headers[tenantToken] = tenantId;
	return config;
});
export const setupResponseInterceptor = (navigate: any) => {
	axios.interceptors.response.use(
		(response) => response,
		(error) => {
			if (error.response.status === FORBIDDEN) {
				return navigate(urlWithTenant('/unauthorized'));
			}

			return Promise.reject(error);
		},
	);
};
const defaultHeader = {};
const callApi = async <T, E>(
	url: string,
	method: Method.GET | Method.POST | Method.PUT | Method.PATCH | Method.DELETE,
	queryParams: any,
	contentType: string,
	headers: { [key: string]: string },
	body: unknown | undefined = undefined,
) => {
	const queryParam = Object.keys(queryParams)
		.map((k) => `${k}=${encodeURIComponent(queryParams[k])}`)
		.join('&');
	const headersToSend = { ...headers, 'Content-Type': contentType };
	const urlWithQueryParams =
		url + (queryParam.length > 0 ? `?${queryParam}` : '');
	try {
		let resp: AxiosResponse<T>;
		switch (method) {
			case Method.GET:
				resp = await axios.get(urlWithQueryParams, { headers: headersToSend });
				break;
			case Method.POST:
				resp = await axios.post(urlWithQueryParams, body, {
					headers: headersToSend,
				});
				break;
			case Method.PUT:
				resp = await axios.put(urlWithQueryParams, body, {
					headers: headersToSend,
				});
				break;
			case Method.PATCH:
				resp = await axios.patch(urlWithQueryParams, body, {
					headers: headersToSend,
				});
				break;
			case Method.DELETE:
				resp = await axios.delete(urlWithQueryParams, {
					data: body,
					headers: headersToSend,
				});
				break;
			default:
				resp = await axios.get(urlWithQueryParams, { headers: headersToSend });
				break;
		}
		return {
			statusCode: resp.status,
			isError: false,
			responseBody: resp.data,
			headers: resp?.headers,
		};
	} catch (e) {
		if (axios.isAxiosError(e)) {
			const error = e as AxiosError<E>;
			if (error?.response?.status === UNAUTHORIZED) {
				storageCleanAndRedirect(UNAUTHORIZED);
				return {
					statusCode: 0,
					isError: true,
					error: e,
				};
			}
			return {
				statusCode: error.response?.status ?? 0,
				isError: true,
				errorBody: error.response?.data,
				error: e,
			};
		}
		// this is not an axios error, rather an unknown error
		return {
			statusCode: 0,
			isError: true,
			error: e,
		};
	}
};

const apiWrapper = {
	get: async <T = unknown, E = unknown>(
		url: string,
		queryParams: unknown = {},
		contentType = CONTENT_TYPE.JSON,
		headers: { [key: string]: string } = defaultHeader,
	): Promise<ApiResponse<T, E>> =>
		callApi(url, Method.GET, queryParams, contentType, headers),

	post: async <T = unknown, E = unknown>(
		url: string,
		body: unknown,
		contentType = CONTENT_TYPE.JSON,
		headers: { [key: string]: string } = defaultHeader,
	): Promise<ApiResponse<T, E>> =>
		callApi(url, Method.POST, {}, contentType, headers, body),

	put: async <T = unknown, E = unknown>(
		url: string,
		body: unknown,
		contentType = CONTENT_TYPE.JSON,
		headers: { [key: string]: string } = defaultHeader,
	): Promise<ApiResponse<T, E>> =>
		callApi(url, Method.PUT, {}, contentType, headers, body),

	patch: async <T = unknown, E = unknown>(
		url: string,
		body: unknown,
		contentType = CONTENT_TYPE.JSON,
		headers: { [key: string]: string } = defaultHeader,
	): Promise<ApiResponse<T, E>> =>
		callApi(url, Method.PATCH, {}, contentType, headers, body),

	delete: async <T = unknown, E = unknown>(
		url: string,
		body: unknown,
		queryParams: { [key: string]: string | number } = {},
		contentType = CONTENT_TYPE.JSON,
		headers: { [key: string]: string } = defaultHeader,
	): Promise<ApiResponse<T, E>> =>
		callApi(url, Method.DELETE, queryParams, contentType, headers, body),
};

export default apiWrapper;
